/**
 * Entries per lottery (§16.2, §16.3 masked by default, §17.4 reason codes never standalone guilt): masked
 * contact, status, source, platform, payment state, risk signals; disqualify with reason + evidence (typed
 * confirmation; sequences never renumber, §9.3); participant profile link; engagement review queue link.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EntryAdminRowFullDto, EntryStatus } from '@windrop/contracts';
import { Button, Field, Input, Select, useIntl } from '@windrop/ui';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { Can } from '../../auth/guards';
import { ConfirmDestructive } from '../../components/ConfirmDestructive';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { FilterBar } from '../../components/FilterBar';
import { MaskedCell } from '../../components/MaskedCell';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { StatusBadge, humanizeStatus } from '../../components/StatusBadge';
import { lotteriesApi } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';
import { entriesApi } from '../../lib/api/entries';

const STATUSES: EntryStatus[] = [
  'PENDING',
  'ELIGIBLE',
  'SNAPSHOT_INCLUDED',
  'INELIGIBLE',
  'REFUNDED',
  'DISQUALIFIED',
];

export function EntriesPage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const intl = useIntl();
  const qc = useQueryClient();
  const status = (params.get('status') ?? '') as EntryStatus | '';
  const source = params.get('source') ?? '';
  const lottery = useQuery({
    queryKey: ['lottery', id],
    queryFn: () => lotteriesApi.get(id),
    enabled: Boolean(id),
  });
  const summary = useQuery({
    queryKey: ['lottery', id, 'entries', 'summary'],
    queryFn: () => entriesApi.summary(id),
    enabled: Boolean(id),
  });
  const list = useQuery({
    queryKey: ['lottery', id, 'entries', { status, source }],
    queryFn: () =>
      entriesApi.list(id, {
        limit: 100,
        ...(status ? { status } : {}),
        ...(source ? { source: source as 'FREE' | 'PAID' | 'BONUS' } : {}),
      }),
    enabled: Boolean(id),
  });
  const [target, setTarget] = useState<EntryAdminRowFullDto | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [evidence, setEvidence] = useState('');
  const disqualify = useMutation({
    mutationFn: ({ entryId, reason }: { entryId: string; reason: string }) =>
      entriesApi.disqualify(entryId, {
        reason,
        ...(evidence.trim() ? { evidenceRef: evidence.trim() } : {}),
      }),
    onSuccess: () => {
      setTarget(null);
      setConfirmOpen(false);
      setEvidence('');
      void qc.invalidateQueries({ queryKey: ['lottery', id, 'entries'] });
    },
  });
  const title = lottery.data
    ? (lottery.data.config.presentation.content[lottery.data.config.identity.locales[0] ?? 'en']?.title ??
      lottery.data.slug)
    : 'Lottery';

  const columns: Column<EntryAdminRowFullDto>[] = [
    {
      id: 'entryId',
      header: 'Entry',
      cell: (r) => <code>{r.entryId.slice(-8)}</code>,
      sortValue: (r) => r.entryId,
      width: '7rem',
    },
    {
      id: 'participant',
      header: 'Participant',
      cell: (r) => <Link to={`/participants/${r.participantId}`}>{r.displayName}</Link>,
      sortValue: (r) => r.displayName.toLowerCase(),
    },
    {
      id: 'contact',
      header: 'Contact (masked)',
      cell: (r) => (
        <MaskedCell
          masked={r.contactMasked}
          subject={{
            kind: 'PARTICIPANT_CONTACT',
            participantId: r.participantId,
            channelId: r.participantId,
          }}
          what="contact"
        />
      ),
      filterValue: (r) => r.contactMasked,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => <StatusBadge kind="EntryStatus" value={r.status} />,
      sortValue: (r) => r.status,
    },
    {
      id: 'source',
      header: 'Source',
      cell: (r) =>
        `${humanizeStatus(r.source)}${r.sourcePlatform ? ` · ${humanizeStatus(r.sourcePlatform)}` : ''}`,
      sortValue: (r) => r.source,
    },
    {
      id: 'seq',
      header: 'Position',
      cell: (r) => r.participantSequence ?? '—',
      sortValue: (r) => r.participantSequence ?? null,
      align: 'end',
    },
    {
      id: 'payment',
      header: 'Payment',
      cell: (r) =>
        r.paymentStatus ? (
          <StatusBadge kind="PaymentStatus" value={r.paymentStatus} />
        ) : (
          <span className="wd-muted">—</span>
        ),
      hideable: true,
    },
    {
      id: 'risk',
      header: 'Signals',
      cell: (r) =>
        r.riskFlags.length ? (
          <span title="Risk signals are reasons for review, never proof (§17.4)">
            {r.riskFlags.map((f) => f.code).join(', ')}
          </span>
        ) : (
          <span className="wd-muted">none</span>
        ),
      filterValue: (r) => r.riskFlags.map((f) => f.code).join(' '),
    },
    {
      id: 'reason',
      header: 'Reason',
      cell: (r) => r.statusReason ?? <span className="wd-muted">—</span>,
      hideable: true,
      defaultHidden: true,
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: (r) => intl.formatDateTime(r.createdAt),
      sortValue: (r) => r.createdAt,
    },
  ];

  if (list.isError) {
    return (
      <ErrorState
        title="Entries could not be loaded"
        body="The lottery may belong to another tenant or the API is unavailable."
        reference={isApiError(list.error) ? list.error.supportReference : undefined}
        action={<Button onClick={() => void list.refetch()}>Retry</Button>}
      />
    );
  }
  const rows = list.data?.items ?? [];
  const counts = summary.data;
  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: 'Lotteries', to: '/lotteries' },
          { label: title, to: `/lotteries/${id}` },
          { label: 'Entries' },
        ]}
        title="Entries"
        description="Masked by default. Disqualification needs a reason and evidence; positions are never renumbered."
        actions={
          <Can permission="engagement_tasks.manage">
            <Button variant="secondary" href={`/engagement?lotteryId=${id}`}>
              Engagement review queue
            </Button>
          </Can>
        }
      />
      <div className="wdc-stats" data-testid="entries-summary">
        <StatCard
          label="Confirmed entries"
          value={counts ? (counts.byStatus.ELIGIBLE ?? 0) + (counts.byStatus.SNAPSHOT_INCLUDED ?? 0) : null}
          loading={summary.isPending}
          hint="ELIGIBLE + snapshot included"
        />
        <StatCard
          label="Pending payment"
          value={counts ? (counts.byStatus.PENDING ?? 0) : null}
          loading={summary.isPending}
          hint="Not in the draw (§10.2)"
        />
        <StatCard
          label="Unique participants"
          value={counts ? counts.uniqueParticipants : null}
          loading={summary.isPending}
          hint="§8.1 positions assigned"
        />
        <StatCard
          label="Bonus entries"
          value={counts ? (counts.bySource.BONUS ?? 0) : null}
          loading={summary.isPending}
          hint="Qualified tasks only (§12.4)"
        />
        <StatCard
          label="Risk-flagged profiles"
          value={counts ? counts.riskFlagged : null}
          loading={summary.isPending}
          hint="Signals for review, not guilt"
        />
      </div>
      <FilterBar>
        <Field label="Status">
          <Select
            options={[
              { value: '', label: 'All' },
              ...STATUSES.map((s) => ({ value: s, label: humanizeStatus(s) })),
            ]}
            value={status}
            onChange={(e) =>
              setParams((p) => {
                const n = new URLSearchParams(p);
                if (e.target.value) n.set('status', e.target.value);
                else n.delete('status');
                return n;
              })
            }
            data-testid="entries-status-filter"
          />
        </Field>
        <Field label="Source">
          <Select
            options={[
              { value: '', label: 'All' },
              { value: 'FREE', label: 'Free' },
              { value: 'PAID', label: 'Paid' },
              { value: 'BONUS', label: 'Bonus' },
            ]}
            value={source}
            onChange={(e) =>
              setParams((p) => {
                const n = new URLSearchParams(p);
                if (e.target.value) n.set('source', e.target.value);
                else n.delete('source');
                return n;
              })
            }
          />
        </Field>
      </FilterBar>
      <DataTable
        caption={`Entries of ${title}`}
        columns={columns}
        rows={rows}
        rowKey={(r) => r.entryId}
        loading={list.isPending}
        emptyState={
          <EmptyState
            title="No entries yet"
            body="Entries appear here as participants enter; pending payments are listed but are not in the draw."
            action={<Button href={`/lotteries/${id}`}>Back to the lottery</Button>}
          />
        }
        initialSort={{ columnId: 'createdAt', direction: 'desc' }}
        density="compact"
        testId="entries-table"
        rowActions={(r) => (
          <Can permission="lottery.update">
            {r.status === 'ELIGIBLE' || r.status === 'SNAPSHOT_INCLUDED' ? (
              <Button variant="danger" onClick={() => setTarget(r)} data-testid={`disqualify-${r.entryId}`}>
                Disqualify
              </Button>
            ) : null}
          </Can>
        )}
      />
      {list.data?.page.nextCursor ? (
        <p className="wd-muted">
          Showing the latest {rows.length} entries; narrow with the filters to see older ones.
        </p>
      ) : null}
      {target ? (
        <div className="wdc-panel wdc-narrow" data-testid="disqualify-evidence-panel">
          <p>
            Disqualify entry <code>{target.entryId}</code> ({target.displayName}, {target.contactMasked}).
            Step 1: evidence reference. Step 2: typed confirmation with the reason.
          </p>
          <Field
            label="Evidence reference"
            hint="Case id, review note id or export reference recorded with the disqualification (never the raw document)."
          >
            <Input
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
              data-testid="disqualify-evidence"
            />
          </Field>
          <div className="wd-btn-group">
            <Button variant="danger" onClick={() => setConfirmOpen(true)} data-testid="disqualify-continue">
              Continue to confirmation
            </Button>
            <Button variant="quiet" onClick={() => setTarget(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
      <ConfirmDestructive
        open={target !== null && confirmOpen}
        onOpenChange={(o) => {
          if (!o) setConfirmOpen(false);
        }}
        title="Disqualify entry"
        objectName={target?.entryId ?? ''}
        objectKind="entry id"
        consequence="The entry leaves the draw pool with the reason recorded and audited. The participant's position and every other position stay as they are. This cannot be undone."
        requireReason
        busy={disqualify.isPending}
        error={
          disqualify.isError
            ? isApiError(disqualify.error)
              ? disqualify.error.message
              : 'Disqualification failed'
            : null
        }
        onConfirm={({ reason }) => {
          if (target) disqualify.mutate({ entryId: target.entryId, reason: reason ?? '' });
        }}
      />
    </>
  );
}
